import { useNavigate, useParams } from "react-router-dom";
import SectionCard from "@/components/base/SectionCard";
import StatusBadge from "@/components/base/StatusBadge";
import { useToast } from "@/components/base/Toast";
import {
  getOrderStatusConfig,
  ORDER_SOURCE_CONFIG,
  PAYMENT_CONFIG,
  PICKUP_CONFIG,
} from "@/constants/status";
import { useStore } from "@/context/StoreContext";
import type { OrderStatus } from "@/types";
import { formatSAR } from "@/utils/format";
import { calcIncludedVat } from "@/utils/vat";
import InvoiceView from "./components/InvoiceView";
import OrderActivityLog from "./components/OrderActivityLog";
import OrderDebtAlert from "./components/OrderDebtAlert";

const ACTION_STATUSES: OrderStatus[] = [
  "preparing",
  "almost",
  "ready",
  "received",
  "missed",
];

// «توصيل بواسطة المحل» orders follow a dedicated flow and never offer pickup statuses.
const DELIVERY_ACTION_STATUSES: OrderStatus[] = [
  "preparing",
  "almost",
  "ready",
  "outForDelivery",
  "received",
];

const NEXT_STATUS: Partial<Record<OrderStatus, OrderStatus>> = {
  new: "preparing",
  preparing: "almost",
  almost: "ready",
  ready: "received",
};

const DELIVERY_NEXT_STATUS: Partial<Record<OrderStatus, OrderStatus>> = {
  new: "preparing",
  preparing: "almost",
  almost: "ready",
  ready: "outForDelivery",
  outForDelivery: "received",
};

const NEXT_ICON: Partial<Record<OrderStatus, string>> = {
  preparing: "ri-fire-line",
  almost: "ri-timer-line",
  ready: "ri-shopping-bag-3-line",
  outForDelivery: "ri-e-bike-2-line",
  received: "ri-checkbox-circle-line",
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { show } = useToast();
  const { orders, updateOrderStatus, appOrderFee } = useStore();

  const order = orders.find((item) => item.id === id) ?? null;

  if (!order) {
    return (
      <div className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card px-8 py-20 text-center">
        <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-file-search-line text-3xl"></i>
        </div>
        <h2 className="text-lg font-bold text-foreground-950">
          الطلب غير موجود
        </h2>
        <p className="mt-1 text-sm text-foreground-500">
          تعذّر العثور على الطلب المطلوب، ربما تم حذفه.
        </p>
        <button
          type="button"
          onClick={() => navigate("/orders")}
          className="mt-5 inline-flex items-center gap-2 rounded-btn bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-line"></i>
          العودة للطلبات
        </button>
      </div>
    );
  }

  const payment = PAYMENT_CONFIG[order.payment];
  const pickup = PICKUP_CONFIG[order.pickup];
  const source = ORDER_SOURCE_CONFIG[order.source];
  const subtotal = order.lines.reduce(
    (sum, line) => sum + line.quantity * line.price,
    0,
  );
  const appFee = order.appFee ?? 0;
  const deliveryFee = order.deliveryFee ?? 0;
  // Total VAT included in the whole order (products + the fixed app fee).
  // Orders that provide an explicit VAT value use it directly.
  const vat = order.vat ?? calcIncludedVat(order.amount);
  const isCash = order.payment === "cash";
  // A cash order that was never received has no completed sale, so no final invoice exists.
  const isMissedCash = isCash && order.status === "missed";
  const invoiceIssued = !isCash || order.status === "received";
  const isDelivery = order.pickup === "delivery";
  const statusLabel = (status: OrderStatus): string =>
    getOrderStatusConfig(status, order.pickup).label;
  const nextStatus = isDelivery
    ? DELIVERY_NEXT_STATUS[order.status]
    : NEXT_STATUS[order.status];
  const actionStatuses = isDelivery
    ? DELIVERY_ACTION_STATUSES
    : ACTION_STATUSES;
  const pendingCashDebt =
    isCash && order.source === "app" && !order.debtRecorded;

  const handleStatusChange = (status: OrderStatus) => {
    const { recorded } = updateOrderStatus(order.id, status);
    if (recorded) {
      show(
        `تم بدء التحضير وتسجيل رسوم التطبيق المستحقة لطلبات الكاش بقيمة ${formatSAR(appOrderFee)}`,
        "warning",
      );
      return;
    }
    show(
      `تم تحديث حالة الطلب #${order.id} إلى «${statusLabel(status)}»`,
    );
  };

  const handleInvoice = () => {
    if (!invoiceIssued) {
      show("الفاتورة النهائية تصدر بعد تأكيد استلام الطلب النقدي", "warning");
      return;
    }
    navigate(`/orders/${order.id}/invoice`);
  };

  return (
    <div className="space-y-4">
      <div className="yc-screen-only space-y-4">
        <div className="yc-fade-up flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/orders")}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              aria-label="رجوع"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-foreground-950">
                  طلب #{order.id}
                </h2>
                <StatusBadge status={order.status} pickup={order.pickup} />
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${source.className}`}
                >
                  <i className={source.icon}></i>
                  {source.short}
                </span>
              </div>
              <p className="mt-1 text-sm text-foreground-500">{order.date}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {invoiceIssued ? (
              <>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 rounded-btn bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-printer-line"></i>
                  طباعة الفاتورة
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/orders/${order.id}/invoice`)}
                  className="inline-flex items-center gap-2 rounded-btn border border-background-200 bg-white px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-eye-line"></i>
                  عرض الفاتورة
                </button>
              </>
            ) : isMissedCash ? null : (
              <button
                type="button"
                onClick={handleInvoice}
                className="inline-flex items-center gap-2 rounded-btn border border-background-200 bg-background-100 px-4 py-2.5 text-sm font-semibold text-foreground-400 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-edit-line"></i>
                إصدار فاتورة
              </button>
            )}
          </div>
        </div>

        {isCash && order.source === "app" && order.status !== "received" && (
          <div className="yc-fade-up">
            <OrderDebtAlert order={order} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SectionCard
            title="بيانات العميل"
            icon="ri-user-3-line"
            className="yc-fade-up"
          >
            <dl className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">الاسم</dt>
                <dd className="font-medium text-foreground-900">
                  {order.customer}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">الجوال</dt>
                <dd dir="ltr" className="font-medium text-foreground-900">
                  {order.phone}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">الفرع</dt>
                <dd className="font-medium text-foreground-900">
                  {order.branch}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">مصدر الطلب</dt>
                <dd className="inline-flex items-center gap-1.5 font-medium text-foreground-900">
                  <i className={source.icon}></i>
                  {source.label}
                </dd>
              </div>
            </dl>
          </SectionCard>

          <SectionCard
            title="الاستلام والدفع"
            icon="ri-shopping-bag-3-line"
            iconClass="bg-accent-100 text-accent-800"
            className="yc-fade-up"
          >
            <dl className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">الاستلام</dt>
                <dd className="inline-flex items-center gap-1.5 font-medium text-foreground-900">
                  <i className={pickup.icon}></i>
                  {pickup.label}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">الدفع</dt>
                <dd className="inline-flex items-center gap-1.5 font-medium text-foreground-900">
                  <i className={payment.icon}></i>
                  {payment.label}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-foreground-500">الفاتورة</dt>
                {invoiceIssued ? (
                  <dd dir="ltr" className="font-medium text-foreground-900">
                    INV-{order.id}
                  </dd>
                ) : isMissedCash ? (
                  <dd className="text-left">
                    <span className="inline-flex items-start gap-1.5 text-xs font-semibold text-foreground-500">
                      <i className="ri-draft-line mt-0.5"></i>
                      مسودة — لم تصدر فاتورة نهائية لعدم استلام الطلب وسداد
                      قيمته
                    </span>
                  </dd>
                ) : (
                  <dd>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-2.5 py-1 text-xs font-semibold text-foreground-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-foreground-400"></span>
                      مسودة
                    </span>
                  </dd>
                )}
              </div>
            </dl>
          </SectionCard>

          {order.note && (
            <SectionCard
              title="ملاحظة العميل"
              icon="ri-sticky-note-line"
              iconClass="bg-secondary-200 text-secondary-800"
              className="yc-fade-up lg:col-span-1"
            >
              <p className="text-sm leading-7 text-foreground-700">
                {order.note}
              </p>
            </SectionCard>
          )}
        </div>

        <SectionCard
          title="محتوى الطلب"
          subtitle={`${order.lines.length} منتجات — ${order.items} قطعة`}
          icon="ri-shopping-cart-2-line"
          className="yc-fade-up"
          bodyClassName="p-0"
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-right border-collapse">
              <thead>
                <tr className="text-xs text-foreground-500 bg-background-50">
                  <th className="font-medium px-5 py-3 whitespace-nowrap">#</th>
                  <th className="font-medium px-3 py-3 whitespace-nowrap">
                    المنتج
                  </th>
                  <th className="font-medium px-3 py-3 whitespace-nowrap">
                    الكمية
                  </th>
                  <th className="font-medium px-3 py-3 whitespace-nowrap">
                    سعر الوحدة
                  </th>
                  <th className="font-medium px-5 py-3 whitespace-nowrap">
                    الإجمالي
                  </th>
                </tr>
              </thead>
              <tbody>
                {order.lines.map((line, index) => (
                  <tr
                    key={`${line.name}-${index}`}
                    className="border-t border-background-100"
                  >
                    <td className="px-5 py-3.5 text-sm text-foreground-500">
                      {index + 1}
                    </td>
                    <td className="px-3 py-3.5 text-sm font-medium text-foreground-900">
                      {line.name}
                    </td>
                    <td className="px-3 py-3.5 text-sm text-foreground-700">
                      {line.quantity}
                    </td>
                    <td className="px-3 py-3.5 text-sm text-foreground-700">
                      {formatSAR(line.price)}
                    </td>
                    <td className="px-5 py-3.5 text-sm font-semibold text-foreground-900">
                      {formatSAR(line.quantity * line.price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end border-t border-background-100 px-5 py-4">
            <div className="w-full max-w-xs space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">المجموع الفرعي</span>
                <span className="text-foreground-800">{formatSAR(subtotal)}</span>
              </div>
              {deliveryFee > 0 && (
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-foreground-500">
                    <i className="ri-e-bike-2-line"></i>
                    رسوم التوصيل
                    <span className="text-[11px] text-foreground-400">
                      (محفوظة وقت إنشاء الطلب)
                    </span>
                  </span>
                  <span className="text-foreground-800">
                    {formatSAR(deliveryFee)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">
                  ضريبة القيمة المضافة (15%)
                </span>
                <span className="text-foreground-800">{formatSAR(vat)}</span>
              </div>
              {appFee > 0 && (
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-foreground-500">
                    <i className="ri-smartphone-line"></i>
                    رسوم التطبيق
                    <span className="text-[11px] text-foreground-400">
                      (ثابتة — تُحدَّد من الإدارة)
                    </span>
                  </span>
                  <span className="text-foreground-800">
                    {formatSAR(appFee)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1 border-t border-dashed border-background-200">
                <span className="font-bold text-foreground-950">الإجمالي</span>
                <span className="text-base font-bold text-primary-700">
                  {formatSAR(order.amount)}
                </span>
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="سجل نشاط الطلب"
          subtitle={`${order.activity.length} حركة مسجّلة`}
          icon="ri-history-line"
          iconClass="bg-secondary-200 text-secondary-800"
          className="yc-fade-up"
        >
          <OrderActivityLog activity={order.activity} />
        </SectionCard>

        {!isMissedCash && (
        <SectionCard
          title="تحديث حالة الطلب"
          icon="ri-refresh-line"
          className="yc-fade-up"
        >
          {pendingCashDebt && (
            <div className="mb-4 flex items-start gap-3 rounded-box border border-accent-200 bg-accent-50 p-4">
              <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-accent-500 text-white">
                <i className="ri-information-line"></i>
              </span>
              <p className="text-sm leading-7 text-accent-900">
                هذا طلب دفع نقدي من التطبيق. عند الضغط على «بدء التحضير» ستُسجَّل
                رسوم التطبيق المستحقة لطلبات الكاش بقيمة{" "}
                <span className="font-bold">{formatSAR(appOrderFee)}</span> على
                المحل — ولا تُسجَّل عند استلام الطلب. أما مديونية العميل على
                محفظته فتُحتسب فقط في حال عدم استلامه للطلب.
              </p>
            </div>
          )}

          {nextStatus && (
            <button
              type="button"
              onClick={() => handleStatusChange(nextStatus)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-btn bg-primary-500 px-6 py-3 text-sm font-bold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className={NEXT_ICON[nextStatus] ?? "ri-arrow-left-line"}></i>
              {nextStatus === "preparing"
                ? "بدء التحضير"
                : `المتابعة إلى «${statusLabel(nextStatus)}»`}
            </button>
          )}

          {!nextStatus && (
            <p className="mb-4 text-sm text-foreground-500">
              الطلب في حالة نهائية. يمكنك تعديل الحالة يدويًا من الأزرار أدناه
              عند الحاجة.
            </p>
          )}

          <div className="mt-5 pt-5 border-t border-background-100">
            <p className="mb-3 text-xs font-semibold text-foreground-500">
              تحديث مباشر للحالة
            </p>
            <div className="flex flex-wrap gap-2">
              {actionStatuses.map((status) => {
                const active = order.status === status;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => handleStatusChange(status)}
                    className={`rounded-btn px-4 py-2 text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                      active
                        ? "bg-primary-500 text-white"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {statusLabel(status)}
                  </button>
                );
              })}
            </div>
          </div>
        </SectionCard>
        )}
      </div>

      <div className="yc-print-only">
        <InvoiceView order={order} />
      </div>
    </div>
  );
}